import {
  ButtonComponentActionResultTypenames,
  ButtonComponentV2,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import { useLogGQLErrorScreenAnalytics } from 'features/moveMoney/screens/dcf/hooks/useLogGQLErrorScreenAnalytics';
import TabaPayAddressScreen from 'features/moveMoney/screens/dcf/tabapay/TabapayAddress/TabaPayAddressScreen';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import useUpdateExternalCardAddress from './hooks/useUpdateExternalCardAddress';

const UpdateAddress = () => {
  const router = useRouter();
  const { cardId } = useGlobalSearchParams<{
    cardId: string;
  }>();

  const {
    isLoading,
    errorScreen,
    updateDebitCardAddress,
    addressText,
    setUserCardDetailsAddress,
  } = useUpdateExternalCardAddress();

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const updateAddress = async (retry?: boolean) => {
    if (hasSubmitted && !retry) {
      return;
    }
    setHasSubmitted(true);
    await updateDebitCardAddress(cardId);
  };

  useEffect(() => {
    if (addressText?.addressLine1 && !hasSubmitted) {
      void updateAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressText, hasSubmitted]);

  useEffect(() => {
    return () => {
      setUserCardDetailsAddress({});
      setHasSubmitted(false);
    };
  }, [setUserCardDetailsAddress]);

  useBackHandler(navigateBack);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'updateExpiryDate',
  });

  const { segmentClient } = useAnalyticsConfiguration();
  const { top } = useSafeAreaInsets();

  useLogGQLErrorScreenAnalytics(
    errorScreen?.analytics,
    'Update Address',
    'card_management',
  );

  const handleErrorButtonPress = (button?: ButtonComponentV2) => {
    const action = button?.buttonOnClick;

    switch (action?.__typename) {
      case ButtonComponentActionResultTypenames.RouteNavigationAction:
        router.replace(action.route as Href);
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: 'card_management',
          });
        }
        break;

      case ButtonComponentActionResultTypenames.SDContinueClientFlowAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: 'card_management',
          });
        }
        void updateAddress(true);
        break;

      case ButtonComponentActionResultTypenames.SDBackNavigationAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: 'card_management',
          });
        }
        navigateBack();
        break;
    }
  };
  return (
    <>
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          errorScreen ? (
            <View style={styles.errorHeader}>
              <View style={[styles.closeIconWrapper, { top: top }]}>
                <IconButton
                  iconKey="CLOSE"
                  iconSize={24}
                  iconOnPress={() => {
                    navigateBack();
                  }}
                  iconButtonAccessibilityLabel="close"
                />
              </View>
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={errorScreen.messageHeadline}
                messageBody={errorScreen.messageBody}
                primaryButton={{
                  title:
                    errorScreen.primaryButton?.buttonText ||
                    translatedText('closeButton'),
                  onPress: () => {
                    if (errorScreen.primaryButton?.buttonText) {
                      handleErrorButtonPress(errorScreen.primaryButton);
                    } else {
                      navigateBack();
                    }
                  },
                }}
                secondaryButton={
                  errorScreen.secondaryButton
                    ? {
                        title: errorScreen.secondaryButton.buttonText,
                        onPress: () =>
                          handleErrorButtonPress(errorScreen.secondaryButton),
                      }
                    : undefined
                }
              />
            </View>
          ) : null
        }
      />
      <View style={styles.screen}>
        <Stack.Screen
          options={{
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} primaryOnPress={navigateBack} />
            ),
          }}
        />
        <TabaPayAddressScreen screenType="card-details" />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  closeIconWrapper: {
    left: theme.spacing.xs,
    position: 'absolute',
    zIndex: 10,
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default UpdateAddress;
